import { useEffect, useState } from 'react';
import type { Action, OnboardingState } from './state.js';
import { pairingCode } from './state.js';

interface Props {
  state: OnboardingState;
  dispatch: React.Dispatch<Action>;
  onNext: () => void;
  onBack: () => void;
}

export function Step3Appairage({ state, dispatch, onNext }: Props) {
  const [paired, setPaired] = useState(state.pairingDone);
  const code = pairingCode(state.foyerNom || 'cadran');

  useEffect(() => {
    if (paired) return;
    const t = setTimeout(() => {
      setPaired(true);
      dispatch({ type: 'PAIRING_DONE' });
    }, 9000);
    return () => clearTimeout(t);
  }, [paired, dispatch]);

  const INSTRUCTIONS = [
    'Allumez la tablette Cadran.',
    "L'écran affiche « Entrez le code d'appairage ».",
    'Saisissez le code ci-dessus.',
  ];

  return (
    <div
      className="container"
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}
    >
      <div style={{ position: 'sticky', top: '160px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>
          Étape 2 — Appairage
        </div>
        <h1
          className="display"
          style={{ fontSize: 'clamp(42px, 4.5vw, 64px)', margin: '0 0 20px' }}
        >
          Associez <em>la tablette</em>.
        </h1>
        <p
          className="serif"
          style={{
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '18px',
            lineHeight: 1.4,
            color: 'var(--ink-soft)',
            marginBottom: '24px',
          }}
        >
          Un code unique lie votre foyer à cet écran.
        </p>
        <div
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--hairline-soft)',
            borderLeft: '3px solid var(--terracotta)',
            borderRadius: '0 8px 8px 0',
            padding: '16px 20px',
          }}
        >
          <p className="small" style={{ margin: 0 }}>
            <b>Pourquoi ?</b> Ce code cryptographique lie définitivement votre foyer à cette
            tablette. Sans lui, aucune tablette ne peut afficher vos données.
          </p>
        </div>
      </div>

      <div className="card">
        {/* Code panel */}
        <div
          style={{
            background: 'oklch(0.14 0.012 60)',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            marginBottom: '28px',
          }}
        >
          <div className="eyebrow" style={{ color: 'oklch(0.62 0.04 60)', marginBottom: '16px' }}>
            Code d'appairage
          </div>
          <div
            style={{
              fontFamily: 'JetBrains Mono Variable, monospace',
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: 500,
              letterSpacing: '0.08em',
              color: 'oklch(0.92 0.014 78)',
            }}
          >
            {code}
          </div>
        </div>

        <ol style={{ paddingLeft: '20px', margin: '0 0 24px', display: 'grid', gap: '10px' }}>
          {INSTRUCTIONS.map((step) => (
            <li key={step} style={{ fontSize: '15px', color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              {step}
            </li>
          ))}
        </ol>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            borderRadius: '8px',
            background: paired ? 'var(--moss-soft)' : 'oklch(0.97 0.016 50)',
            border: `1px solid ${paired ? 'var(--moss)' : 'var(--terracotta-soft)'}`,
            marginBottom: '24px',
          }}
        >
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: paired ? 'var(--moss)' : 'var(--terracotta)',
              animation: paired ? 'none' : 'pulse 1.6s ease-in-out infinite',
              flexShrink: 0,
            }}
          />
          <span
            className="small"
            style={{ color: paired ? 'var(--moss)' : 'var(--terracotta-deep)' }}
          >
            {paired ? 'Tablette associée ✓' : 'En attente de la tablette…'}
          </span>
        </div>
        <style>{'@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }'}</style>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="button" className="btn btn-secondary" onClick={onNext} style={{ flex: 1 }}>
            Passer cette étape
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onNext}
            disabled={!paired}
            style={{ flex: 1 }}
          >
            Continuer
          </button>
        </div>
      </div>
    </div>
  );
}
